'use client';

import {
  CoreBadge,
  CoreBadgeProps,
  CoreBadgeVariant,
} from '@woolworthslimited/core-components-react/badge';
import { CoreIcon } from '@woolworthslimited/core-components-react/icon';
import {
  CoreButtonProps,
  CoreButtonVariant,
} from '@woolworthslimited/core-components-react/shared/types/button.types';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';

import styles from './button.module.scss';

const coreButtonPrefix = 'core-button-';
const badgeVariantMap: Record<CoreButtonVariant, CoreBadgeVariant> = {
  primary: 'contrast',
  secondary: 'neutral',
  tertiary: 'primary',
  destructive: 'contrast',
};

function CoreButton({
  as = 'button',
  badge,
  buttonRefHandler,
  clickHandler,
  isDisabled = false,
  isFullWidth = false,
  icon,
  iconPosition = 'leading',
  isLoading = false,
  label,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  isTabbingDisabled = false,
  size = 'medium',
  variant = 'primary',
  extendedProps,
}: CoreButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (
    label &&
    as === 'a' &&
    !icon &&
    extendedProps?.target?.toLowerCase() === '_blank'
  ) {
    icon = { name: 'icon-navigation-external-link', size };
    iconPosition = 'trailing';
  }

  const buttonStyles = clsx(
    styles['core-button'],
    styles[`${coreButtonPrefix}${variant}`],
    styles[`${coreButtonPrefix}${size}`],
    isFullWidth && styles[`${coreButtonPrefix}full-width`],
    isLoading && [
      styles[`${coreButtonPrefix}is-loading`],
      styles[`${coreButtonPrefix}leading`],
    ],
    icon &&
      (!label
        ? styles[`${coreButtonPrefix}icon-only`]
        : styles[`${coreButtonPrefix}${iconPosition}`]),
    !icon && badge && [styles[`${coreButtonPrefix}badge`]]
  );

  useEffect(() => {
    buttonRefHandler?.(buttonRef);
  });

  const finalAriaLabel =
    isLoading && !label && !ariaLabel ? 'Loading' : ariaLabel;

  const badgeProps = badge as CoreBadgeProps | undefined;
  const finalBadgeProps: CoreBadgeProps | undefined =
    badge && !icon
      ? {
          ...badgeProps,
          variant: badgeProps?.variant ?? badgeVariantMap[variant],
          isDisabled: isDisabled || badgeProps?.isDisabled,
        }
      : undefined;

  const buttonTemplateBase = (
    elementType: keyof React.JSX.IntrinsicElements,
    elementProps: any = {}
  ) => {
    return React.createElement(
      elementType,
      {
        ref: buttonRef,
        className: buttonStyles,
        disabled: isDisabled,
        'aria-label': finalAriaLabel,
        'aria-expanded': ariaExpanded,
        'aria-controls': ariaControls,
        ...(isTabbingDisabled ? { tabIndex: -1 } : {}),
        onClick: clickHandler,
        ...elementProps,
        ...extendedProps,
      },
      isLoading ? (
        <CoreIcon name="icon-status-loading" size={size} ariaHidden={true} />
      ) : (
        icon && <CoreIcon {...icon} ariaHidden={true} />
      ),
      label && <span aria-hidden={!!ariaLabel}>{label}</span>,
      finalBadgeProps && <CoreBadge {...finalBadgeProps} aria-hidden="true" />
    );
  };

  const buttonTemplate = () => buttonTemplateBase('button', { type: as });

  const linkButtonTemplate = () => buttonTemplateBase('a');

  return as === 'a' ? linkButtonTemplate() : buttonTemplate();
}

export default CoreButton;
